import * as React from "react";
import { Agency } from "../../messages";
import { CaregiverStatus } from "../../messages/CaregiverStatus";
import { AgencyId, AgencyMemberId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { AgencyWithChatOnlyDetails } from "../../system/AppState/AppAgenciesState";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { HashMap } from "../../utils/HashMap";
import { NOT_FOUND_ICON } from "../../utils/svgs_new";
import { undefinedToNull } from "../../utils/undefinedToNull";
import EmptyScaffoldBody from "../EmptyScaffoldBody/EmptyScaffoldBody";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import { If } from "../Generic/If";
import { IsDefined } from "../Generic/IsDefined";
import PromptBottomSheet, { PromptBottomSheetOption } from "../PromptBottomSheet/PromptBottomSheet";
import {
  AgencyMembershipStatus,
  caregiverStatusToAgencyMembershipStatus,
  convertToAgenciesBlocks,
  toAgencyBlock,
} from "./AgenciesListUtils";
import AgenciesTabs from "./AgenciesTabs";
import { ChatWithAgencyBottomSheet } from "./components/ChatWithAgencyBottomSheet";
import ChatWithAgencyCard from "./components/ChatWithAgencyCard";

export interface AgenciesListProps {
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>;
  selectedAgencyId?: AgencyId;
  selectedDepartment?: string;
  agencyIdToCancel?: AgencyId;
  activeTab: AgencyMembershipStatus;
  onRequestCloseChatWithAgency: () => void;
  onRequestCloseCancelRequestPrompt: () => void;
  onClickOpenCancelPrompt: (agencyId: AgencyId) => void;
  onClickDepartment: (department: string) => void;
  onClickTab: (tab: AgencyMembershipStatus) => void;
  onClickChatWithAgency: (agencyId: Agency) => void;
  onClickChatWithAgencyMember: (agencyMemberId: AgencyMemberId) => void;
  onClickCancel: (agencyId: AgencyId) => void;
}

const AgenciesList = (props: AgenciesListProps) => {
  const agenciesBlocks = React.useMemo(() => {
    return convertToAgenciesBlocks(props.agencies);
  }, [props.agencies]);

  const selectedAgency = undefinedToNull(
    fmap(props.selectedAgencyId, (x) => props.agencies.get(x))
  );

  const shouldHideCard = (caregiverStatus: CaregiverStatus) => {
    const status = caregiverStatusToAgencyMembershipStatus(caregiverStatus);

    return status !== props.activeTab;
  };

  const agenciesBlockBySelectedTab = React.useMemo(() => {
    return agenciesBlocks.find(
      (x) => caregiverStatusToAgencyMembershipStatus(x.status) === props.activeTab
    );
  }, [agenciesBlocks, props.activeTab]);

  const handleChooseCancelPrompt = React.useCallback(
    (shouldCancelAgency: boolean) => {
      if (shouldCancelAgency && props.agencyIdToCancel !== undefined) {
        props.onClickCancel.call(null, props.agencyIdToCancel);
      }
    },
    [props.onClickCancel, props.agencyIdToCancel]
  );

  return (
    <div className="AgenciesList">
      <AgenciesTabs activeTab={props.activeTab} onClick={props.onClickTab} />
      {agenciesBlocks.map((agenciesBlock) => (
        <div key={agenciesBlock.status} className="__agency-tab-container">
          {agenciesBlock.agencies.zipWithIndex().map(([agency, i]) => (
            <AppSlideFadeIn
              key={agency.agencyDetails.id.toString()}
              style={{
                display: shouldHideCard(agenciesBlock.status) ? "none" : "block",
              }}
              delay={150 + i * 100}
            >
              <ChatWithAgencyCard
                agency={agency}
                status={agenciesBlock.status}
                onClickChatWithAgency={props.onClickChatWithAgency}
                onClickCancel={props.onClickOpenCancelPrompt}
              />
            </AppSlideFadeIn>
          ))}
        </div>
      ))}
      <If condition={agenciesBlockBySelectedTab === undefined}>
        <EmptyScaffoldBody
          icon={NOT_FOUND_ICON}
          label={translate(STRINGS.THERE_SEEMS_TO_BE_NOTHING_HERE)}
        />
      </If>
      <IsDefined value={selectedAgency}>
        {(definedSelectedAgency) => (
          <ChatWithAgencyBottomSheet
            agency={toAgencyBlock(definedSelectedAgency)}
            activeDepartment={props.selectedDepartment}
            isOpen={true}
            onRequestClose={props.onRequestCloseChatWithAgency}
            onClickDepartment={props.onClickDepartment}
            onClickAgencyMember={props.onClickChatWithAgencyMember}
          />
        )}
      </IsDefined>
      <PromptBottomSheet
        title={translate(STRINGS.WARNING)}
        message={translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_THIS_REQUEST)}
        options={cancelPromptOptions}
        isOpen={props.agencyIdToCancel !== undefined}
        onChoose={handleChooseCancelPrompt}
        onRequestClose={props.onRequestCloseCancelRequestPrompt}
      />
    </div>
  );
};

export default React.memo(AgenciesList);

const cancelPromptOptions: PromptBottomSheetOption[] = [
  { value: false, label: translate(STRINGS.NO) },
  { value: true, label: translate(STRINGS.YES) },
];
